import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { compose, withHandlers, withProps } from 'recompose';

import { Link, Flexer, Spacer, Tag } from '@ontruck/orc';

import tel from 'utils/phone';
import DetailTitle from 'modules/Common/DetailTitle';

import {
  VehicleMaxWeight,
  VehicleType,
  VehicleMaxPallets,
  VehicleExtras,
  VehicleMaxHeight,
} from 'modules/Vehicle/Details';

import {
  assignDriver,
  changeDriver,
  changeVehicle,
  selectVehicle,
  openCarrier,
  openDriver,
  viewCarrierVehicles,
  copyAssignationMessage,
} from 'redux/modules/job-detail/effects';

import { StyledFlexChild, StyledFlexParent } from './styled';

import CopyButton from 'modules/Common/Copy/CopyButton';
import { DRIVER_STATES } from 'components/CarrierDetails/Constants';
import { ServiceTagFleetService } from 'components/ServiceTag';

const enhance = compose(
  injectIntl,
  connect(null, {
    assignDriver,
    changeDriver,
    changeVehicle,
    selectVehicle,
    openCarrier,
    openDriver,
    viewCarrierVehicles,
    copyAssignationMessage,
  }),
  withProps(({ vehicles, vehicle }) => ({
    realVehicle:
      (vehicles || []).length > 1
        ? (vehicles || []).find((v) => v.vehicle_type !== 'tractor_unit')
        : vehicle,
  })),
  withHandlers({
    handleCarrierClick:
      ({ openCarrier, actions, carrier }) =>
      () =>
        openCarrier && openCarrier({ carrier_id: carrier.id }),
    handleDriverClick:
      ({ openDriver, carrier, driver }) =>
      () =>
        openDriver && openDriver({ carrier_id: carrier.id, driver_id: driver.id }),
    handleCarrierVehiclesClick:
      ({ viewCarrierVehicles, actions, carrier }) =>
      () =>
        viewCarrierVehicles && viewCarrierVehicles({ carrier_id: carrier.id }),
  })
);

const DriverInfo = React.memo(
  ({
    intl,
    isInvalidVehicle,
    driver,
    fleet_owner: fleetOwner,
    vehicles,
    carrier,
    realVehicle,
    handleDriverClick,
    handleCarrierClick,
    handleCarrierVehiclesClick,
    route,
    changeDriver,
    changeVehicle,
    selectVehicle,
    copyAssignationMessage,
    dedicated_contract: isDedicated,
    previous_unfinished_journey_ids,
  }) => {
    const t = intl.messages.modules.journey.detail.driver_info;
    return (
      <>
        <div>
          <StyledFlexParent>
            <StyledFlexChild>
              <DetailTitle>{t.driver_title}</DetailTitle>
              {driver.status !== DRIVER_STATES.removed && (
                <div>
                  <Link text={driver.contact_name} onClick={handleDriverClick} />
                </div>
              )}
              {driver.status === DRIVER_STATES.removed && <div>{driver.contact_name}</div>}
              <div>
                <FormattedMessage
                  id="jobs"
                  defaultMessage={t.jobs}
                  values={{
                    number: String(driver.shipments),
                  }}
                />
              </div>
              <div>
                <Link text={driver.contact_phone} href={tel(driver.contact_phone)} />
              </div>
              {driver?.is_fleet_service && <ServiceTagFleetService />}
              {!!isDedicated && (
                <Tag skin="modernGrey" weight="bold" textTransform="Uppercase">
                  {t.dedicated}
                </Tag>
              )}
              <Spacer mTop="XS">
                <CopyButton text={t.copy_assignation} onClick={copyAssignationMessage} />
              </Spacer>
              <Spacer mTop="XS">
                <Link text={t.change_driver} weight="bold" onClick={changeDriver} />
              </Spacer>
            </StyledFlexChild>
            <StyledFlexChild>
              <DetailTitle>{t.vehicle_title}</DetailTitle>
              {!!vehicles && (
                <Fragment>
                  <Flexer.Parent alignItems="center">
                    <Spacer mRight="XS">
                      {vehicles.map((v) => (
                        <div key={v.id}>
                          <VehicleType {...v} /> - {v.plate_number}
                        </div>
                      ))}
                    </Spacer>
                    <Spacer>
                      <VehicleExtras {...realVehicle} />
                    </Spacer>
                  </Flexer.Parent>
                  <Flexer.Parent alignItems="center">
                    <Spacer mRight="XS">
                      <VehicleMaxPallets country={(route || {}).country} {...realVehicle} />
                    </Spacer>
                    <Spacer mRight="XS">
                      <VehicleMaxWeight {...realVehicle} />
                    </Spacer>
                    <Spacer>
                      <VehicleMaxHeight {...realVehicle} />
                    </Spacer>
                  </Flexer.Parent>
                  {isInvalidVehicle && (
                    <Fragment>
                      <Spacer mTop="XS">
                        <Tag text={t.cannot_do_the_job} skin="red" weight="bold" />
                      </Spacer>
                      <Spacer mTop="XS">
                        <Link
                          text={t.view_carrier_vehicles}
                          weight="bold"
                          skin="secondary"
                          onClick={handleCarrierVehiclesClick}
                        />
                      </Spacer>
                    </Fragment>
                  )}
                  <Spacer mTop="XS">
                    <Link text={t.change_vehicle} weight="bold" onClick={changeVehicle} />
                  </Spacer>
                </Fragment>
              )}
              {!vehicles && (
                <Fragment>
                  <Tag text={t.no_vehicle} skin="red" weight="bold" />
                  <Spacer mTop="S">
                    <Link
                      text={t.view_carrier_vehicles}
                      weight="bold"
                      skin="secondary"
                      onClick={handleCarrierVehiclesClick}
                    />
                  </Spacer>
                  <Spacer mTop="XS">
                    <Link text={t.select_vehicle} weight="bold" onClick={selectVehicle} />
                  </Spacer>
                </Fragment>
              )}
            </StyledFlexChild>
            <StyledFlexChild>
              <DetailTitle>{t.carrier_title}</DetailTitle>
              <div>
                <Link text={carrier.name} onClick={handleCarrierClick} />
              </div>
              <div>
                <Link text={carrier.phone} href={tel(carrier.phone)} />
              </div>
            </StyledFlexChild>
            <StyledFlexChild>
              <DetailTitle>{t.related_jobs_title}</DetailTitle>
              {previous_unfinished_journey_ids.length ? (
                <Spacer mTop="XS">
                  <Tag text={t.has_blocking_jobs_tag} skin="red" weight="bold" />
                  <Spacer mTop="XS">
                    {previous_unfinished_journey_ids.map((journeyId) => (
                      <div data-testid="blocking-journey">
                        <Link text={`J${journeyId}`} weight="bold" href={`/jobs/${journeyId}`} />
                      </div>
                    ))}
                  </Spacer>
                </Spacer>
              ) : (
                <Spacer mTop="XS">
                  <Tag text={t.has_no_blocking_jobs_tag} skin="green" weight="bold" />
                </Spacer>
              )}
            </StyledFlexChild>
          </StyledFlexParent>
          <Flexer.Parent justifyContent="flex-start">
            {t.fleet_owner}: {fleetOwner ? fleetOwner.name : t.no_owner}
          </Flexer.Parent>
        </div>
      </>
    );
  }
);

DriverInfo.propTypes = {
  intl: PropTypes.object.isRequired,
  driver: PropTypes.object,
  vehicle: PropTypes.object,
  vehicles: PropTypes.array,
  carrier: PropTypes.object,
  isInvalidVehicle: PropTypes.bool,
  status: PropTypes.string,
  totals: PropTypes.object,
  realVehicle: PropTypes.any,
  handleCarrierClick: PropTypes.func,
  handleDriverClick: PropTypes.func,
  handleCarrierVehiclesClick: PropTypes.func,
  route: PropTypes.object,
  previous_unfinished_journey_ids: PropTypes.array,
};

DriverInfo.defaultProps = {
  driver: {},
  vehicle: {},
  vehicles: [],
  carrier: {},
  totals: {},
  route: {},
  previous_unfinished_journey_ids: [],
};

export default enhance(DriverInfo);
